/**
 * QAICoachPage (F4) - v3.0 Bolt/Claude Inspired
 * Main Q AI Coach interface for standalone users
 *
 * Route: /q-ai/coach (protected - requires Q AI authentication)
 * Target: Retail MT5 traders who signed up via Q AI standalone
 *
 * Key Improvements v3.0:
 * - Pure dark background (#0A0A0F) like Bolt.new
 * - Crisp white text (#F5F5F5)
 * - Minimal header design
 * - Professional institutional styling
 */

import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Tooltip, Chip } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import useQChat from '../../hooks/useQChat';
import QChatWindow from '../../Components/QAI/QChatWindow';
import QAnalysisBoard from '../../Components/QAI/QAnalysisBoard';
import PricingModal from '../../Components/QAI/PricingModal';
import QAILogo from '../../Components/QAI/QAILogo';
import TokenBalance from '../../Components/QAI/TokenBalance';
import QBridgeSetupModal, { shouldShowQBridgeSetup } from '../../Components/QAI/QBridgeSetupModal';
import QBridgeHelpModal from '../../Components/QAI/QBridgeHelpModal';
import QBridgeStatusBanner from '../../Components/QAI/QBridgeStatusBanner';
import { getSyncStatus } from '../../api/qaiApi';

/**
 * QAICoachPage Component
 * Full-screen Q AI chat interface for standalone users
 */
const QAICoachPage = () => {
  const navigate = useNavigate();

  // Get Q AI token and user from localStorage
  // In production, this would come from a QAI-specific auth context
  const [qaiToken] = useState(() => localStorage.getItem('qai_token') || localStorage.getItem('token'));
  const [qaiUser] = useState(() => {
    const userStr = localStorage.getItem('qai_user') || localStorage.getItem('user');
    try {
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  });

  const userName = qaiUser?.firstName || qaiUser?.name || 'Trader';

  // QBridge connection status (real API integration)
  const [qbridgeStatus, setQbridgeStatus] = useState({
    connected: false,
    lastSync: null,
    tradeCount: 0
  });

  // QBridge modal states
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Analysis Board data state
  const [analysisData, setAnalysisData] = useState(null);
  const [isAnalysisLive, setIsAnalysisLive] = useState(false);
  const [lastAnalysisUpdate, setLastAnalysisUpdate] = useState(null);

  // Handle analysis data from AI responses
  const handleAnalysisData = useCallback((data) => {
    setAnalysisData(data);
    setIsAnalysisLive(true);
    setLastAnalysisUpdate(new Date().toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    }));

    // Turn off "live" indicator after 30 seconds
    setTimeout(() => setIsAnalysisLive(false), 30000);
  }, []);

  // Initialize Q Chat hook
  const {
    messages,
    balance,
    isLoading,
    showPricingModal,
    error,
    isLowBalance,
    canSend,
    sendMessage,
    handleActionCard,
    handleActionChip,
    refreshBalance,
    cancelStream,
    clearMessages,
    closePricingModal
  } = useQChat(qaiToken, userName, handleAnalysisData);

  // Ref to track if balance has been fetched
  const balanceFetched = useRef(false);

  // Redirect to login if no token
  useEffect(() => {
    if (!qaiToken) {
      navigate('/q-ai/login');
    }
  }, [qaiToken, navigate]);

  // Fetch balance on mount
  useEffect(() => {
    if (qaiToken && !balanceFetched.current) {
      balanceFetched.current = true;
      refreshBalance();
    }
  }, [qaiToken, refreshBalance]);

  // Fetch real QBridge sync status from API
  useEffect(() => {
    const checkSyncStatus = async () => {
      if (!qaiToken) return;

      try {
        const status = await getSyncStatus(qaiToken);
        setQbridgeStatus({
          connected: status.hasSynced || false,
          lastSync: status.lastSyncAt || null,
          tradeCount: status.tradeCount || 0
        });
      } catch (err) {
        console.error('Failed to fetch sync status:', err);
        // Keep default disconnected state on error
      }
    };

    checkSyncStatus();
  }, [qaiToken]);

  // Check if should show QBridge setup modal on first visit
  useEffect(() => {
    if (qaiToken && shouldShowQBridgeSetup()) {
      // Small delay to let the page render first
      const timer = setTimeout(() => {
        setShowSetupModal(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [qaiToken]);

  // Handle logout
  const handleLogout = useCallback(() => {
    localStorage.removeItem('qai_token');
    localStorage.removeItem('qai_user');
    navigate('/q-ai');
  }, [navigate]);

  // Handle MT5 chip click - opens setup modal
  const handleMT5Click = useCallback(() => {
    if (qbridgeStatus.connected) {
      // If connected, could show status or sync options
      console.log('MT5 already connected, last sync:', qbridgeStatus.lastSync);
    } else {
      // If not connected, open setup modal
      setShowSetupModal(true);
    }
  }, [qbridgeStatus.connected, qbridgeStatus.lastSync]);

  // Handle QBridge setup completion - refresh sync status from API
  const handleSetupComplete = useCallback(async () => {
    // Update local state immediately for responsive UI
    setQbridgeStatus({
      connected: true,
      lastSync: new Date().toISOString(),
      tradeCount: 0
    });

    // Refresh sync status from API to get real data
    if (qaiToken) {
      try {
        const status = await getSyncStatus(qaiToken);
        setQbridgeStatus({
          connected: status.hasSynced || true,
          lastSync: status.lastSyncAt || new Date().toISOString(),
          tradeCount: status.tradeCount || 0
        });
      } catch (err) {
        console.error('Failed to refresh sync status:', err);
      }
    }
  }, [qaiToken]);

  // Handle help request - sends message to chat
  const handleAskQBridgeHelp = useCallback(() => {
    // Send installation help message to chat (FREE prompt)
    sendMessage('Help me install QBridge', 'chat');
  }, [sendMessage]);

  // Handle buy tokens
  const handleBuyTokens = useCallback(() => {
    // Show pricing modal
    // Note: The modal is controlled by showPricingModal from useQChat
  }, []);

  // Handle tier selection from pricing modal
  const handleSelectTier = useCallback(async (tierId) => {
    console.log('Selected tier:', tierId);
    closePricingModal();
  }, [closePricingModal]);

  // Don't render if no token
  if (!qaiToken) {
    return null;
  }

  return (
    <Box
      sx={{
        height: '100vh',
        backgroundColor: '#0A0A0F', // Pure dark like Bolt.new
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* ═══════════════════════════════════════════════════════════════════════
          HEADER - Minimal Bolt-style
          ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        component="header"
        sx={{
          backgroundColor: '#0A0A0F',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          px: { xs: 2, md: 3 },
          py: 1,
          flexShrink: 0
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            maxWidth: '1400px',
            margin: '0 auto',
            width: '100%'
          }}
        >
          {/* Left: Logo + QBridge Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <QAILogo size="small" showText textVariant="short" />

            {/* Inline QBridge Status - Minimal */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                icon={qbridgeStatus.connected ? <CheckCircleIcon /> : <ErrorIcon />}
                label={qbridgeStatus.connected ? 'MT5 Connected' : 'MT5 Disconnected'}
                size="small"
                onClick={handleMT5Click}
                sx={{
                  backgroundColor: 'transparent',
                  color: qbridgeStatus.connected ? '#27B56D' : '#EF4444',
                  border: `1px solid ${qbridgeStatus.connected
                    ? 'rgba(39, 181, 109, 0.2)'
                    : 'rgba(239, 68, 68, 0.2)'}`,
                  fontSize: '0.6875rem',
                  height: '24px',
                  cursor: 'pointer',
                  '& .MuiChip-icon': {
                    color: qbridgeStatus.connected ? '#27B56D' : '#EF4444',
                    fontSize: '0.8125rem'
                  },
                  '&:hover': {
                    backgroundColor: qbridgeStatus.connected
                      ? 'rgba(39, 181, 109, 0.08)'
                      : 'rgba(239, 68, 68, 0.08)'
                  }
                }}
              />
            </Box>
          </Box>

          {/* Right Side Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Token Balance */}
            <TokenBalance
              balance={balance}
              isLow={isLowBalance}
              onBuyTokens={handleBuyTokens}
              size="small"
            />

            {/* Help */}
            <Tooltip title="Help & Setup Guide" arrow>
              <IconButton
                onClick={() => setShowHelpModal(true)}
                size="small"
                sx={{
                  color: '#71717A',
                  width: 32,
                  height: 32,
                  '&:hover': {
                    color: '#7c3aed',
                    backgroundColor: 'rgba(124, 58, 237, 0.08)'
                  }
                }}
              >
                <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>

            {/* Settings */}
            <Tooltip title="Settings" arrow>
              <IconButton
                size="small"
                sx={{
                  color: '#71717A',
                  width: 32,
                  height: 32,
                  '&:hover': {
                    color: '#27B56D',
                    backgroundColor: 'rgba(39, 181, 109, 0.08)'
                  }
                }}
              >
                <SettingsIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>

            {/* Logout */}
            <Tooltip title="Logout" arrow>
              <IconButton
                onClick={handleLogout}
                size="small"
                sx={{
                  color: '#71717A',
                  width: 32,
                  height: 32,
                  '&:hover': {
                    color: '#EF4444',
                    backgroundColor: 'rgba(239, 68, 68, 0.08)'
                  }
                }}
              >
                <LogoutIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          MAIN CONTENT - 2/3 Analysis Board + 1/3 Chat Split
          ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          overflow: 'hidden',
          px: { xs: 1, sm: 2, md: 3 },
          py: { xs: 1, md: 1.5 },
          gap: { xs: 1.5, md: 2 }
        }}
      >
        {/* ═══════════════════════════════════════════════════════════════════════
            LEFT PANEL - Analysis Board (2/3 width on desktop)
            ═══════════════════════════════════════════════════════════════════════ */}
        <Box
          sx={{
            flex: { xs: 1, lg: 2 },
            minHeight: { xs: '40vh', lg: 0 },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            order: { xs: 2, lg: 1 } // On mobile, show chat first
          }}
        >
          <QAnalysisBoard
            analysisData={analysisData}
            isLive={isAnalysisLive}
            lastUpdated={lastAnalysisUpdate}
          />
        </Box>

        {/* ═══════════════════════════════════════════════════════════════════════
            RIGHT PANEL - Chat Window (1/3 width on desktop)
            ═══════════════════════════════════════════════════════════════════════ */}
        <Box
          sx={{
            flex: { xs: 1, lg: 1 },
            minWidth: { lg: '380px' },
            maxWidth: { lg: '450px' },
            minHeight: { xs: '50vh', lg: 0 },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            order: { xs: 1, lg: 2 }, // On mobile, show chat first
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.06)'
          }}
        >
          <QChatWindow
            messages={messages}
            balance={balance}
            isLoading={isLoading}
            isLowBalance={isLowBalance}
            canSend={canSend}
            onSendMessage={sendMessage}
            onActionCard={handleActionCard}
            onActionChip={handleActionChip}
            onCancelStream={cancelStream}
            onBuyTokens={handleBuyTokens}
            onClearChat={clearMessages}
            error={error}
          />
        </Box>
      </Box>

      {/* Pricing Modal */}
      <PricingModal
        open={showPricingModal}
        onClose={closePricingModal}
        onSelectTier={handleSelectTier}
        currentBalance={balance}
      />

      {/* QBridge Setup Modal */}
      <QBridgeSetupModal
        open={showSetupModal}
        onClose={() => setShowSetupModal(false)}
        onSkip={() => setShowSetupModal(false)}
        onComplete={handleSetupComplete}
        userEmail={qaiUser?.email || ''}
      />

      {/* QBridge Help Modal */}
      <QBridgeHelpModal
        open={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        onAskHelp={handleAskQBridgeHelp}
      />
    </Box>
  );
};

export default QAICoachPage;
